import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { SignOut, Image, Envelope, FileText } from '@phosphor-icons/react';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('stats');
  const [token, setToken] = useState('');
  const [stats, setStats] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [newImage, setNewImage] = useState({
    title_en: '',
    title_ar: '',
    image_url: '',
    category: '',
    description_en: '',
    description_ar: ''
  });
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    const adminToken = localStorage.getItem('adminToken');
    if (!adminToken) {
      navigate('/admin');
      return;
    }
    setToken(adminToken);
    fetchStats(adminToken);
  }, []);

  const fetchStats = async (adminToken) => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/stats?token=${adminToken}`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin');
      }
    }
  };

  const fetchInquiries = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/inquiries?token=${token}`);
      setInquiries(response.data.inquiries);
    } catch (error) {
      console.error('Error fetching inquiries:', error);
    }
  };

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/quotes?token=${token}`);
      setQuotes(response.data.quotes);
    } catch (error) {
      console.error('Error fetching quotes:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminEmail');
    navigate('/admin');
  };

  const handleAddImage = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/admin/gallery?token=${token}`, newImage);
      alert('Image added successfully');
      setNewImage({
        title_en: '',
        title_ar: '',
        image_url: '',
        category: '',
        description_en: '',
        description_ar: ''
      });
    } catch (error) {
      alert('Error adding image');
    }
  };

  useEffect(() => {
    if (token && activeTab === 'inquiries') {
      fetchInquiries();
    }
    if (token && activeTab === 'quotes') {
      fetchQuotes();
    }
  }, [activeTab, token]);

  return (
    <div className="min-h-screen bg-[#F5F5F0] pt-20">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="bg-white p-6 mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-light font-heading mb-1">Admin Dashboard</h1>
            <p className="text-sm text-gray-600">{localStorage.getItem('adminEmail')}</p>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="flex items-center px-4 py-2 border border-black hover:bg-black hover:text-white transition-all"
          >
            <SignOut size={20} className="mr-2" />
            Logout
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-black/10 mb-8 bg-white">
          {[
            { id: 'stats', label: 'Statistics', icon: <FileText size={20} /> },
            { id: 'inquiries', label: 'Contact Inquiries', icon: <Envelope size={20} /> },
            { id: 'quotes', label: 'Quote Requests', icon: <FileText size={20} /> },
            { id: 'gallery', label: 'Gallery Management', icon: <Image size={20} /> },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`flex items-center px-6 py-4 text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'border-b-2 border-[#C4A661] text-[#C4A661]'
                  : 'text-gray-500 hover:text-black'
              }`}
            >
              {tab.icon}
              <span className="ml-2">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Stats */}
        {activeTab === 'stats' && stats && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6" data-testid="stats-section">
            {[
              { label: 'Total Inquiries', value: stats.total_inquiries, color: 'bg-blue-500' },
              { label: 'New Inquiries', value: stats.new_inquiries, color: 'bg-green-500' },
              { label: 'Total Quotes', value: stats.total_quotes, color: 'bg-purple-500' },
              { label: 'Pending Quotes', value: stats.pending_quotes, color: 'bg-orange-500' },
              { label: 'Gallery Images', value: stats.total_gallery_images, color: 'bg-pink-500' },
            ].map((stat, index) => (
              <div key={index} className="bg-white p-6 border border-black/5">
                <div className={`w-12 h-12 ${stat.color} rounded-full flex items-center justify-center text-white text-xl font-bold mb-4`}>
                  {stat.value}
                </div>
                <h3 className="text-sm font-medium text-gray-600">{stat.label}</h3>
              </div>
            ))}
          </div>
        )}

        {/* Inquiries */}
        {activeTab === 'inquiries' && (
          <div className="bg-white p-6" data-testid="inquiries-section">
            <h2 className="text-2xl font-light font-heading mb-6">Contact Inquiries</h2>
            {inquiries.length === 0 ? (
              <p className="text-gray-500">No inquiries yet</p>
            ) : (
              <div className="space-y-4">
                {inquiries.map((inquiry, index) => (
                  <div key={index} className="border border-black/5 p-4">
                    <div className="grid grid-cols-2 gap-4 mb-2">
                      <div>
                        <span className="font-medium">Name:</span> {inquiry.name}
                      </div>
                      <div>
                        <span className="font-medium">Email:</span> {inquiry.email}
                      </div>
                      <div>
                        <span className="font-medium">Phone:</span> {inquiry.phone}
                      </div>
                      <div>
                        <span className="font-medium">Date:</span> {new Date(inquiry.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="mt-2">
                      <span className="font-medium">Message:</span>
                      <p className="text-gray-700 mt-1">{inquiry.message}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Quotes */}
        {activeTab === 'quotes' && (
          <div className="bg-white p-6" data-testid="quotes-section">
            <h2 className="text-2xl font-light font-heading mb-6">Quote Requests</h2>
            {quotes.length === 0 ? (
              <p className="text-gray-500">No quote requests yet</p>
            ) : (
              <div className="space-y-4">
                {quotes.map((quote, index) => (
                  <div key={index} className="border border-black/5 p-4">
                    <div className="grid grid-cols-2 gap-4 mb-2">
                      <div>
                        <span className="font-medium">Name:</span> {quote.name}
                      </div>
                      <div>
                        <span className="font-medium">Email:</span> {quote.email}
                      </div>
                      <div>
                        <span className="font-medium">Phone:</span> {quote.phone}
                      </div>
                      <div>
                        <span className="font-medium">Date:</span> {new Date(quote.created_at).toLocaleDateString()}
                      </div>
                      <div>
                        <span className="font-medium">Project Type:</span> {quote.project_type}
                      </div>
                      <div>
                        <span className="font-medium">Materials:</span> {quote.materials_needed}
                      </div>
                    </div>
                    <div className="mt-2">
                      <span className="font-medium">Description:</span>
                      <p className="text-gray-700 mt-1">{quote.project_description}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Gallery Management */}
        {activeTab === 'gallery' && (
          <div className="bg-white p-6" data-testid="gallery-section">
            <h2 className="text-2xl font-light font-heading mb-6">Add Gallery Image</h2>
            <form onSubmit={handleAddImage} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Title (English)</label>
                  <input
                    type="text"
                    required
                    value={newImage.title_en}
                    onChange={(e) => setNewImage({ ...newImage, title_en: e.target.value })}
                    data-testid="image-title-en"
                    className="w-full px-4 py-2 border border-black/20 focus:border-[#C4A661] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Title (Arabic)</label>
                  <input
                    type="text"
                    required
                    value={newImage.title_ar}
                    onChange={(e) => setNewImage({ ...newImage, title_ar: e.target.value })}
                    data-testid="image-title-ar"
                    className="w-full px-4 py-2 border border-black/20 focus:border-[#C4A661] focus:outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Image URL</label>
                <input
                  type="url"
                  required
                  value={newImage.image_url}
                  onChange={(e) => setNewImage({ ...newImage, image_url: e.target.value })}
                  data-testid="image-url"
                  className="w-full px-4 py-2 border border-black/20 focus:border-[#C4A661] focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Category</label>
                <select
                  required
                  value={newImage.category}
                  onChange={(e) => setNewImage({ ...newImage, category: e.target.value })}
                  data-testid="image-category"
                  className="w-full px-4 py-2 border border-black/20 focus:border-[#C4A661] focus:outline-none"
                >
                  <option value="">Select category</option>
                  <option value="residential">Residential</option>
                  <option value="hospitality">Hospitality</option>
                  <option value="corporate">Corporate</option>
                  <option value="malls">Malls & Retail</option>
                  <option value="materials">Materials</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Description (English)</label>
                  <textarea
                    rows="3"
                    value={newImage.description_en}
                    onChange={(e) => setNewImage({ ...newImage, description_en: e.target.value })}
                    className="w-full px-4 py-2 border border-black/20 focus:border-[#C4A661] focus:outline-none"
                  ></textarea>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Description (Arabic)</label>
                  <textarea
                    rows="3"
                    value={newImage.description_ar}
                    onChange={(e) => setNewImage({ ...newImage, description_ar: e.target.value })}
                    className="w-full px-4 py-2 border border-black/20 focus:border-[#C4A661] focus:outline-none"
                  ></textarea>
                </div>
              </div>
              <button
                type="submit"
                data-testid="add-image-submit"
                className="px-8 py-3 bg-[#0F0F0F] text-white text-sm font-medium hover:bg-[#C4A661] transition-all"
              >
                Add Image
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
